/**
 * Instructor Quiz List Page
 * Displays list of quizzes created by the instructor
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiPlus, FiClock, FiAlertCircle, FiFileText, FiUsers, FiHelpCircle, FiCalendar, FiCheckCircle, FiEdit3, FiEye, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';
import EditAssignmentModal from '../../components/modals/EditAssignmentModal';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import SuccessModal from '../../components/modals/SuccessModal';
import ResetStudentQuizModal from '../../components/modals/ResetStudentQuizModal';
import UnassignQuizModal from '../../components/modals/UnassignQuizModal';
import QuizFiltersBar from '../../components/quizzes/QuizFiltersBar';
import QuizListItem from '../../components/quizzes/QuizListItem';
import QuizListSkeleton from '../../components/quizzes/QuizListSkeleton';

const AssignmentsContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 3rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    padding: 1rem 0.75rem 2rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.5rem 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding: 1.5rem;
  background: var(--background-primary);
  border: 1px solid rgba(216, 184, 137, 0.22);
  border-radius: 24px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    padding: 1.25rem;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 18px;
  }
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const HeaderSubtitle = styled.div`
  display: none;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;

  @media (max-width: 640px) {
    display: block;
  }
`;

const MobileTopPanel = styled.div`
  @media (max-width: 640px) {
    background: #f7f5f1;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 1rem;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: clamp(1.5rem, 2.2vw, 2rem);
  font-weight: 800;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  margin: 0;
  letter-spacing: -0.04em;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.375rem;
  }
`;

// Help Tab Styled Components
const HelpContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 0;
  
  @media (max-width: 768px) {
    padding: 1rem 0;
  }
`;

const HelpHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const HelpTitle = styled.h1`
  color: var(--text-primary);
  font-size: clamp(1.75rem, 2.5vw + 1rem, 2.25rem);
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const HelpSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1.125rem;
  margin: 0;
`;

const HelpSection = styled.div`
  margin-bottom: 3rem;
  background: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const HelpSectionTitle = styled.h2`
  color: var(--text-primary);
  font-size: clamp(1.25rem, 2vw + 1rem, 1.5rem);
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HelpStep = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-start;
`;

const StepNumber = styled.div`
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: var(--text-inverse);
  width: 2rem;
  height: 2rem;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const StepDescription = styled.div`
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  
  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }
  
  li {
    margin-bottom: 0.25rem;
  }
`;


const HelpTroubleshooting = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const TroubleshootingItem = styled.div`
  padding: 1.5rem;
  background: rgba(246, 224, 94, 0.1);
  border-radius: 0;
  border-left: 4px solid var(--warning-color);
`;

const TroubleshootingTitle = styled.h4`
  color: var(--warning-dark);
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const TroubleshootingSolution = styled.p`
  color: var(--warning-dark);
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.5;
`;





// const FilterButton = styled.button`
//   padding: 0.5rem 1rem;
//   border: 1px solid var(--border-color);
//   background: ${props => props.active ? 'var(--primary-color)' : 'var(--background-primary)'};
//   color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
//   border-radius: 0;
//   font-size: 0.875rem;
//   font-weight: 500;
//   cursor: pointer;
//   transition: var(--transition);
//   font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

//   &:hover {
//     background: ${props => props.active ? 'var(--primary-hover)' : 'var(--background-tertiary)'};
//     border-color: ${props => props.active ? 'var(--primary-hover)' : 'var(--border-dark)'};
//     color: ${props => props.active ? 'white' : 'var(--text-primary)'};
//   }
// `;

const AssignmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  min-width: 0;
`;

const AssignmentCard = styled(motion.div)`
  background: #fff;
  border: 0.5px solid #d9d9d9;
  border-radius: 12px;
  box-shadow: none;
  overflow: hidden;
  position: relative;
  padding: 20px 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    box-shadow: none;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    border-radius: 18px;
    margin-bottom: 1rem;
    padding: 20px;
  }
`;

const AssignmentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 12px;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 20px 20px 0;
    flex-direction: column;
    align-items: stretch;
  }
`;

const AssignmentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const AssignmentTitle = styled.h3`
  color: #1f2937;
  font-size: 15px;
  font-weight: 500;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.025em;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 0.375rem;
    white-space: normal;
  }
`;

const CourseInfo = styled.p`
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    font-size: 0.8125rem;
    margin-bottom: 0.5rem;
  }
`;

const AssignmentTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  margin-bottom: 3px;
`;

const AssignedStats = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const StatColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px;
  text-align: center;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }

  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const StatDivider = styled.div`
  width: 0.5px;
  height: 36px;
  background: #e5e7eb;
  flex-shrink: 0;
`;

const StatValue = styled.div`
  color: #111827;
  font-size: 18px;
  font-weight: 500;
  line-height: 1;
  margin-bottom: 3px;
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.15;
  white-space: nowrap;
`;

const StatusBadge = styled.div`
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  white-space: nowrap;
  flex-shrink: 0;
  
  ${props => {
    switch (props.status) {
      case 'published':
        return `
          background: #EAF3DE;
          color: #3B6D11;
        `;
      case 'draft':
        return `
          background: linear-gradient(135deg, rgba(246, 224, 94, 0.1) 0%, rgba(214, 158, 46, 0.05) 100%);
          color: var(--warning-dark);
          border-color: rgba(246, 224, 94, 0.2);
        `;
      default:
        return `
          background: linear-gradient(135deg, rgba(216, 184, 137, 0.15) 0%, rgba(216, 184, 137, 0.08) 100%);
          color: var(--primary-color);
          border-color: rgba(216, 184, 137, 0.3);
        `;
    }
  }}

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.6875rem;
    align-self: flex-start;
  }
`;

const InlineAssignmentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  margin-bottom: 14px;
  color: #4b5563;
  font-size: 12px;
  font-weight: 500;

  @media (max-width: 768px) {
    display: none;
  }
`;

const InlineDue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  svg {
    color: #6b7280;
  }
`;

const InlineDescription = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: italic;
  color: #6b7280;
`;

const InlineSeparator = styled.span`
  color: #d1d5db;
`;

const AssignmentDivider = styled.hr`
  border: none;
  border-top: 0.5px solid #e5e7eb;
  margin: 0 0 14px;
`;

const AssignmentContent = styled.div`
  padding: 0;
  background: #fff;

  @media (max-width: 768px) {
    padding: 0 20px 20px;
  }
`;

const AssignmentDescription = styled.div`
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.25rem;
  font-size: 0.9375rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  /* Ensure all HTML elements use Inter font */
  * {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  p {
    margin: 0 0 0.75rem 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul, ol {
    margin: 0.75rem 0;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.375rem;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    color: var(--text-primary);
    font-weight: 600;
    margin: 1rem 0 0.5rem 0;
    
    &:first-child {
      margin-top: 0;
    }
  }

  strong {
    font-weight: 600;
    color: var(--text-primary);
  }

  em {
    font-style: italic;
  }

  code {
    background-color: var(--background-tertiary);
    padding: 0.125rem 0.375rem;
    border-radius: 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.875em;
  }

  pre {
    background-color: var(--background-secondary);
    padding: 1rem;
    border-radius: 0;
    overflow-x: auto;
    margin: 1rem 0;
    
    code {
      background: none;
      padding: 0;
    }
  }

  @media (max-width: 768px) {
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

const AssignmentDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 1.5rem;
  padding: 0;
  background: transparent;
  border-radius: 0;
  border: none;

  @media (min-width: 769px) {
    display: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    padding: 0;
    margin-bottom: 1.25rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #667085;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  svg {
    color: #6B7280;
    opacity: 0.8;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const AssignmentActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  margin-top: 0;
  padding-top: 0;
  border-top: none;
  align-items: center;

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-top: 0;
    padding-top: 0;
    flex-wrap: wrap;
  }

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const ActionSpacer = styled.div`
  flex: 1;
`;

const ActionButton = styled.button`
  padding: 8px 14px;
  border: 0.5px solid #d1d5db;
  border-radius: 6px;
  background-color: transparent;
  color: #111827;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  min-height: 36px;
  touch-action: manipulation;
  
  &:hover {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &.primary {
    background: #bd9655;
    color: var(--text-inverse);
    border-color: #bd9655;
    font-weight: 500;
    box-shadow: 0 2px 8px rgba(216, 184, 137, 0.3);
    
    &:hover {
      background: var(--primary-dark);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(216, 184, 137, 0.4);
    }
  }
  
  &.secondary {
    background: #fff;
    color: #111827;
    border-color: #d1d5db;
    box-shadow: none;
    
    &:hover {
      background: #f9fafb;
      transform: translateY(-1px);
      box-shadow: none;
    }
  }

  &.danger {
    margin-left: 0;
    background: transparent;
    color: #6b7280;
    border-color: transparent;
    box-shadow: none;
    
    &:hover {
      background: transparent;
      color: var(--error-color);
      transform: none;
      box-shadow: none;
    }

    &:disabled {
      background: rgba(252, 129, 129, 0.2);
      border-color: var(--error-color);
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }

  @media (max-width: 768px) {
    padding: 0.625rem 1rem;
    font-size: 0.8125rem;
    width: 100%;
    justify-content: center;

    &.danger {
      margin-left: 0;
      border-color: transparent;
      color: #6b7280;
    }
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  background: var(--background-primary);
  border: 1px solid rgba(216, 184, 137, 0.18);
  border-radius: 24px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    color: var(--text-primary);
    margin-bottom: 0.5rem;
  }
`;



const ErrorState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--danger-color);
  background: var(--background-primary);
  border: 1px solid rgba(252, 129, 129, 0.28);
  border-radius: 24px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
  }
`;

const TabContainer = styled.div`
  margin-bottom: 2rem;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;

  @media (max-width: 768px) {
    padding: 0;
    border-radius: 0;
  }
`;

const TabList = styled.div`
  display: flex;
  border-bottom: 0.5px solid #d1d5db;
  margin-bottom: 14px;
  gap: 18px;
  padding: 0;
  background: transparent;
  border-radius: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 768px) {
    flex-wrap: nowrap;
    gap: 0.85rem;
    border-bottom: 0.5px solid #d1d5db;
    border-radius: 0;
  }
`;

const Tab = styled.button`
  padding: 0.25rem 0 0.625rem;
  border: none;
  background: transparent;
  color: ${props => props.$active ? '#BA7517' : 'var(--color-text-secondary, var(--text-secondary))'};
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  border-bottom: ${props => props.$active ? '2px solid #BA7517' : '2px solid transparent'};
  transition: var(--transition);
  white-space: nowrap;
  flex: 0 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: ${props => props.$active ? '#BA7517' : 'var(--color-text-primary, var(--text-primary))'};
    background: transparent;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.25rem 0 0.625rem;
    font-size: 13px;
    border-radius: 0;
    flex: 0 0 auto;
    min-width: 0;
  }
  
  @media (max-width: 480px) {
    padding: 0.25rem 0 0.625rem;
    font-size: 13px;
  }
`;

const TabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #f3f4f6;
  border: none;
  color: var(--color-text-secondary, var(--text-secondary));
  font-size: 11px;
  font-weight: 600;

  ${Tab}[data-active='true'] & {
    background: #FAEEDA;
    color: #633806;
  }
`;

const DesktopTabText = styled.span`
  display: inline;
`;

const MobileHelpTab = styled(Tab)`
  margin-left: auto;
  gap: 0.25rem;
  color: #6b7280;
`;

const TabPanel = styled.div`
  display: ${props => props.$active ? 'block' : 'none'};
`;

function InstructorQuizList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [quizzes, setQuizzes] = useState([]);
  const [assignedQuizzes, setAssignedQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignedLoading, setAssignedLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignedError, setAssignedError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Default to showing active courses only; users can select "All Courses" to include previous/inactive courses
  const [courseFilter, setCourseFilter] = useState('active');
  const [sortBy, setSortBy] = useState('recent');
  const [courses, setCourses] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('quizzes');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState(null);
  const [unassignModalOpen, setUnassignModalOpen] = useState(false);
  const [assignmentToUnassign, setAssignmentToUnassign] = useState(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [assignmentToReset, setAssignmentToReset] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successDetails, setSuccessDetails] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchQuizzes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch all quizzes (use a high limit to get all)
      const response = await quizApiService.getInstructorQuizzes({ limit: 1000 });

      if (response.success) {
        setQuizzes(response.data || []);
      } else {
        setError('Failed to fetch quizzes');
      }
    } catch (err) {
      console.error('Error fetching quizzes:', err);
      setError('Failed to fetch quizzes');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      const response = await quizApiService.getCourses();
      if (response.success) {
        setCourses(response.courses || response.data || []);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  }, []);

  const fetchAssignedQuizzes = useCallback(async () => {
    try {
      setAssignedLoading(true);
      setAssignedError(null);
      // Fetch all assigned quizzes (use a high limit to get all)
      const response = await quizApiService.getAssignedQuizzes({ limit: 1000 });

      if (response.success) {
        console.log('Assigned quizzes data:', response.data);
        setAssignedQuizzes(response.data || []);
      } else {
        setAssignedError('Failed to fetch assigned quizzes');
      }
    } catch (err) {
      console.error('Error fetching assigned quizzes:', err);
      setAssignedError('Failed to fetch assigned quizzes');
    } finally {
      setAssignedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuizzes();
    fetchAssignedQuizzes();
    fetchCourses();
  }, [fetchQuizzes, fetchAssignedQuizzes, fetchCourses]);

  // Refresh quizzes when navigating back from quiz creation/editing
  useEffect(() => {
    if (location.pathname === '/instructor/quizzes') {
      // Refresh quizzes when component becomes visible again
      fetchQuizzes();
      fetchAssignedQuizzes();
    }
  }, [location.pathname, fetchQuizzes, fetchAssignedQuizzes]);

  const handleDeleteQuiz = (quiz) => {
    setQuizToDelete(quiz);
    setDeleteModalOpen(true);
  };

  const confirmDeleteQuiz = async () => {
    if (!quizToDelete) return;

    setDeleteLoading(true);
    try {
      await quizApiService.deleteQuiz(quizToDelete.id);
      setQuizzes(prev => prev.filter(q => q.id !== quizToDelete.id));
      setDeleteModalOpen(false);
      setQuizToDelete(null);
    } catch (error) {
      console.error('Error deleting quiz:', error);
      alert('Failed to delete quiz. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDeleteQuiz = () => {
    setDeleteModalOpen(false);
    setQuizToDelete(null);
  };

  const handleUnassignQuiz = (assignment) => {
    setAssignmentToUnassign(assignment);
    setUnassignModalOpen(true);
  };

  const handleConfirmUnassign = async (assignmentIds) => {
    if (!assignmentToUnassign) return;

    try {
      const result = await quizApiService.unassignMultipleQuizzes(assignmentIds);
      
      if (result.success || result.data.successful > 0) {
        // Refresh the assigned quizzes list
        await fetchAssignedQuizzes();
      setUnassignModalOpen(false);
      setAssignmentToUnassign(null);
        
        if (result.data.failed > 0) {
          setSuccessMessage(`Successfully unassigned ${result.data.successful} assignment(s).`);
          setSuccessDetails(`${result.data.failed} assignment(s) failed to unassign.`);
        } else {
          setSuccessMessage(`Successfully unassigned ${result.data.successful} assignment(s).`);
          setSuccessDetails(null);
        }
        setSuccessModalOpen(true);
      } else {
        throw new Error(result.message || 'Failed to unassign quiz');
      }
    } catch (error) {
      console.error('Error unassigning quiz:', error);
      setErrorMessage(error.message || 'Failed to unassign quiz. Please try again.');
      setShowErrorModal(true);
    }
  };

  const cancelUnassignQuiz = () => {
    setUnassignModalOpen(false);
    setAssignmentToUnassign(null);
  };

  const handleResetStudentQuiz = (assignment) => {
    setAssignmentToReset(assignment);
    setResetModalOpen(true);
  };

  const handleConfirmReset = async (assignmentIds) => {
    if (!assignmentToReset) return;

    try {
      const response = await quizApiService.resetStudentQuizAttempts(
        assignmentToReset.quiz_id,
        assignmentIds
      );
      
      if (response.success) {
        // Refresh the assigned quizzes list
        await fetchAssignedQuizzes();
        setResetModalOpen(false);
        setAssignmentToReset(null);
        
        // Use response data if available, otherwise use assignmentIds length
        const count = response.data?.assignmentsReset || assignmentIds.length;
        const studentsCount = response.data?.studentsReset;
        
        if (studentsCount) {
          setSuccessMessage(`Successfully reset ${count} quiz attempt(s) for ${studentsCount} student(s).`);
        } else {
          setSuccessMessage(`Successfully reset ${count} quiz attempt(s).`);
        }
        setSuccessDetails(null);
        setSuccessModalOpen(true);
      } else {
        throw new Error(response.message || 'Failed to reset quiz attempts');
      }
    } catch (error) {
      console.error('Error resetting quiz attempts:', error);
      setErrorMessage(error.message || 'Failed to reset quiz attempts. Please try again.');
      setShowErrorModal(true);
    }
  };

  const cancelResetQuiz = () => {
    setResetModalOpen(false);
    setAssignmentToReset(null);
  };

  const handleEditAssignment = (assignment) => {
    setSelectedAssignment(assignment);
    setEditModalOpen(true);
  };

  const handleSaveAssignment = async (assignmentId, updateData) => {
    try {
      await quizApiService.updateAssignment(assignmentId, updateData);
      
      // Refresh the assigned quizzes list to get updated data
      await fetchAssignedQuizzes();
    } catch (error) {
      console.error('Error updating assignment:', error);
      throw new Error('Failed to update assignment. Please try again.');
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedAssignment(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredAndSortedQuizzes = useMemo(() => {
    // Filter quizzes
    let filtered = quizzes.filter(quiz => {
      const matchesSearch = quiz.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (quiz.course_name && quiz.course_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           (quiz.description && quiz.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      let matchesCourse = false;

      // IMPORTANT: Quizzes without course_id should ALWAYS show up for the instructor who created them
      // regardless of course filter (except when filtering by a specific course)
      const hasNoCourseId = !quiz.course_id || quiz.course_id === 0 || quiz.course_id === null || quiz.course_id === undefined;

      if (courseFilter === 'active') {
        // Show quizzes for active courses OR quizzes without course_id (unassigned quizzes)
        // Quizzes without course_id should always be visible to their creator
        if (hasNoCourseId) {
          matchesCourse = true;
        } else if (quiz.course_id && courses.length > 0) {
          const course = courses.find(c => c.id === parseInt(quiz.course_id));
          matchesCourse = course ? (course.is_active === 1 || course.is_active === true) : false;
        } else {
          // Fallback: if courses not loaded yet, check course_is_active
          // If course_is_active is null/undefined, it means no course (should be shown)
          // If course_is_active is true/1, it's an active course (should be shown)
          matchesCourse = quiz.course_is_active === 1 || quiz.course_is_active === true || 
                         quiz.course_is_active === null || quiz.course_is_active === undefined ||
                         hasNoCourseId; // Also check hasNoCourseId as fallback
        }
      } else if (courseFilter === 'inactive') {
        // Show only quizzes for inactive courses (exclude quizzes without course_id)
        if (hasNoCourseId) {
          matchesCourse = false;
        } else if (quiz.course_id && courses.length > 0) {
          const course = courses.find(c => c.id === parseInt(quiz.course_id));
          if (course) {
            matchesCourse = course.is_active === 0 || course.is_active === false || course.is_active === null || course.is_active === undefined;
          } else {
            matchesCourse = false;
          }
        } else {
          // Fallback to quiz.course_is_active if courses array not loaded or course_id not available
          matchesCourse = quiz.course_is_active === 0 || quiz.course_is_active === false;
        }
      } else if (courseFilter === 'all') {
        // Show all courses (active and inactive) AND quizzes without course_id
        matchesCourse = true;
      } else {
        // courseFilter is a specific course ID - exclude quizzes without course_id
        if (hasNoCourseId) {
          matchesCourse = false;
        } else {
          matchesCourse = (courseFilter && quiz.course_id && parseInt(quiz.course_id) === parseInt(courseFilter)) ||
                          (courseFilter && quiz.course_name && courses.find(c => c.id === parseInt(courseFilter))?.name === quiz.course_name);
        }
      }
      
      return matchesSearch && matchesCourse;
    });

    // Sort quizzes
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
        case 'created':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return filtered;
  }, [quizzes, searchTerm, courseFilter, sortBy, courses]);

  const filteredAssignedQuizzes = useMemo(() => {
    const filtered = assignedQuizzes.filter(assignment => {
      const assignmentTitle = assignment.quiz_title || '';
      const assignmentCourseName = assignment.course_name || '';
      const assignmentCourseCode = assignment.course_code || '';
      const assignmentDescription = assignment.quiz_description || '';
      const normalizedSearchTerm = searchTerm.toLowerCase();
      const matchesSearch = assignmentTitle.toLowerCase().includes(normalizedSearchTerm) ||
                            assignmentCourseName.toLowerCase().includes(normalizedSearchTerm) ||
                            assignmentCourseCode.toLowerCase().includes(normalizedSearchTerm) ||
                            assignmentDescription.toLowerCase().includes(normalizedSearchTerm);
      let matchesCourse = false;
      
      if (courseFilter === 'active') {
        // Show only quizzes for active courses
        // Prioritize checking courses array, fallback to assignment.course_is_active if available
        if (assignment.course_id && courses.length > 0) {
          const course = courses.find(c => c.id === parseInt(assignment.course_id));
          matchesCourse = course ? (course.is_active === 1 || course.is_active === true) : false;
        } else {
          // Fallback to assignment.course_is_active if courses array not loaded or course_id not available
          matchesCourse = assignment.course_is_active === 1 || assignment.course_is_active === true;
        }
      } else if (courseFilter === 'inactive') {
        // Show only quizzes for inactive courses
        // Inactive means is_active is 0, false, or NULL
        if (assignment.course_id && courses.length > 0) {
          const course = courses.find(c => c.id === parseInt(assignment.course_id));
          if (course) {
            matchesCourse = course.is_active === 0 || course.is_active === false || course.is_active === null || course.is_active === undefined;
          } else {
            matchesCourse = false;
          }
        } else {
          // Fallback to assignment.course_is_active if courses array not loaded or course_id not available
          matchesCourse = assignment.course_is_active === 0 || assignment.course_is_active === false || assignment.course_is_active === null || assignment.course_is_active === undefined;
        }
      } else if (courseFilter === 'all') {
        // Show all courses (active and inactive)
        matchesCourse = true;
      } else {
        // courseFilter is a specific course ID
        matchesCourse = (courseFilter && assignment.course_id && parseInt(assignment.course_id) === parseInt(courseFilter)) ||
                        (courseFilter && assignment.course_name && courses.find(c => c.id === parseInt(courseFilter))?.name === assignment.course_name);
      }
      
      return matchesSearch && matchesCourse;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.updated_at || b.assigned_at || b.created_at || 0) - new Date(a.updated_at || a.assigned_at || a.created_at || 0);
        case 'created':
          return new Date(b.created_at || b.assigned_at || 0) - new Date(a.created_at || a.assigned_at || 0);
        case 'title':
          return (a.quiz_title || '').localeCompare(b.quiz_title || '');
        default:
          return 0;
      }
    });

    return filtered;
  }, [assignedQuizzes, searchTerm, courseFilter, sortBy, courses]);

  // Counts for tabs - reflect course filter only (not search term)
  const quizCountByCourseFilter = useMemo(() => {
    return quizzes.filter(quiz => {
      // Handle quizzes without course_id - show them when filter is 'all' or 'active'
      const hasNoCourseId = !quiz.course_id || quiz.course_id === 0 || quiz.course_id === null;

      if (courseFilter === 'active') {
        // Show quizzes for active courses OR quizzes without course_id (unassigned quizzes)
        if (hasNoCourseId) {
          return true;
        } else if (quiz.course_id && courses.length > 0) {
          const course = courses.find(c => c.id === parseInt(quiz.course_id));
          return course ? (course.is_active === 1 || course.is_active === true) : false;
        } else {
          // Fallback to quiz.course_is_active if courses array not loaded or course_id not available
          return quiz.course_is_active === 1 || quiz.course_is_active === true;
        }
      } else if (courseFilter === 'inactive') {
        // Show only quizzes for inactive courses (exclude quizzes without course_id)
        if (hasNoCourseId) {
          return false;
        } else if (quiz.course_id && courses.length > 0) {
          const course = courses.find(c => c.id === parseInt(quiz.course_id));
          if (course) {
            return course.is_active === 0 || course.is_active === false || course.is_active === null || course.is_active === undefined;
          } else {
            return false;
          }
        } else {
          return quiz.course_is_active === 0 || quiz.course_is_active === false || quiz.course_is_active === null || quiz.course_is_active === undefined;
        }
      } else if (courseFilter === 'all') {
        return true;
      } else {
        // courseFilter is a specific course ID - exclude quizzes without course_id
        if (hasNoCourseId) {
          return false;
        } else {
          return (courseFilter && quiz.course_id && parseInt(quiz.course_id) === parseInt(courseFilter)) ||
                 (courseFilter && quiz.course_name && courses.find(c => c.id === parseInt(courseFilter))?.name === quiz.course_name);
        }
      }
    }).length;
  }, [quizzes, courseFilter, courses]);

  const assignedQuizCountByCourseFilter = useMemo(() => {
    return assignedQuizzes.filter(assignment => {
      if (courseFilter === 'active') {
        // Prioritize checking courses array, fallback to assignment.course_is_active if available
        if (assignment.course_id && courses.length > 0) {
          const course = courses.find(c => c.id === parseInt(assignment.course_id));
          return course ? (course.is_active === 1 || course.is_active === true) : false;
        } else {
          return assignment.course_is_active === 1 || assignment.course_is_active === true;
        }
      } else if (courseFilter === 'inactive') {
        // Inactive means is_active is 0, false, or NULL
        if (assignment.course_id && courses.length > 0) {
          const course = courses.find(c => c.id === parseInt(assignment.course_id));
          if (course) {
            return course.is_active === 0 || course.is_active === false || course.is_active === null || course.is_active === undefined;
          } else {
            return false;
          }
        } else {
          return assignment.course_is_active === 0 || assignment.course_is_active === false || assignment.course_is_active === null || assignment.course_is_active === undefined;
        }
      } else if (courseFilter === 'all') {
        return true;
      } else {
        // courseFilter is a specific course ID
        return (courseFilter && assignment.course_id && parseInt(assignment.course_id) === parseInt(courseFilter)) ||
               (courseFilter && assignment.course_name && courses.find(c => c.id === parseInt(courseFilter))?.name === assignment.course_name);
      }
    }).length;
  }, [assignedQuizzes, courseFilter, courses]);


  const renderQuizzesTab = () => (
    <>
      {loading ? (
        <QuizListSkeleton count={5} />
      ) : error ? (
        <ErrorState>
          <FiAlertCircle size={48} />
          <h3>Error loading quizzes</h3>
          <p>{error}</p>
          <button 
            onClick={fetchQuizzes}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--border-radius)',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Retry
          </button>
        </ErrorState>
      ) : filteredAndSortedQuizzes.length > 0 ? (
        <AssignmentsList>
          {filteredAndSortedQuizzes.map((quiz, index) => (
            <QuizListItem
              key={quiz.id}
              quiz={quiz}
              onAssign={() => navigate(`/instructor/quizzes/${quiz.id}/assign`)}
              onEdit={() => navigate(`/instructor/quizzes/${quiz.id}/edit`)}
              onPreview={() => navigate(`/instructor/quizzes/${quiz.id}/preview`)}
              onDelete={() => handleDeleteQuiz(quiz)}
              index={index}
            />
          ))}
        </AssignmentsList>
      ) : (
        <EmptyState>
          <FiFileText size={64} />
          <h3>{searchTerm ? 'No results' : 'No quizzes yet'}</h3>
          <p>
            {searchTerm
              ? 'Try a different search to find what you\'re looking for.'
              : 'Create your first quiz to get started'
            }
          </p>
          {!searchTerm && (
            <button 
              onClick={() => navigate('/instructor/quizzes/new')}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 1.5rem',
                background: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--border-radius)',
                cursor: 'pointer',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                margin: '1.5rem auto 0'
              }}
            >
              <FiPlus size={18} />
              Create Quiz
            </button>
          )}
        </EmptyState>
      )}
    </>
  );

  const renderAssignedQuizzesTab = () => (
    <>
      {assignedLoading ? (
        <QuizListSkeleton count={5} />
      ) : assignedError ? (
        <ErrorState>
          <FiAlertCircle size={48} />
          <h3>Error loading assigned quizzes</h3>
          <p>{assignedError}</p>
          <button 
            onClick={fetchAssignedQuizzes}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--border-radius)',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Retry
          </button>
        </ErrorState>
      ) : filteredAssignedQuizzes.length > 0 ? (
        <AssignmentsList>
          {filteredAssignedQuizzes.map((assignment, index) => {
            const completedAttempts = assignment.completed_attempts || 0;
            const totalAttempts = assignment.total_attempts || assignment.student_count || 0;
            const stats = [
              {
                value: `${completedAttempts}/${totalAttempts}`,
                label: 'completed',
                ariaLabel: `${completedAttempts} of ${totalAttempts} students completed`
              },
              { value: assignment.student_count || 0, label: 'students' },
              {
                value: assignment.time_limit_minutes ? `${assignment.time_limit_minutes} min` : 'No Time Limit',
                label: 'time limit'
              },
              { value: assignment.attempts_allowed || 1, label: 'attempts' }
            ];
            const plainDescription = sanitizeHtmlForViewer(assignment.quiz_description || 'No description provided')
              .replace(/<[^>]*>/g, ' ')
              .replace(/\s+/g, ' ')
              .trim();

            return (
              <AssignmentCard
                key={assignment.assignment_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <AssignmentHeader>
                  <AssignmentInfo>
                    <AssignmentTitleRow>
                      <AssignmentTitle>{assignment.quiz_title}</AssignmentTitle>
                      <StatusBadge status="published">
                        <FiCheckCircle size={12} />
                        Assigned
                      </StatusBadge>
                    </AssignmentTitleRow>
                    <CourseInfo>
                      {assignment.course_name ? `${assignment.course_code} · ${assignment.course_name}` : 'No course assigned'}
                    </CourseInfo>
                  </AssignmentInfo>

                  <AssignedStats>
                    {stats.map((stat, statIndex) => (
                      <React.Fragment key={stat.label}>
                        {statIndex > 0 && <StatDivider />}
                        <StatColumn aria-label={stat.ariaLabel}>
                          <StatValue>{stat.value}</StatValue>
                          <StatLabel>{stat.label}</StatLabel>
                        </StatColumn>
                      </React.Fragment>
                    ))}
                  </AssignedStats>
                </AssignmentHeader>

                <InlineAssignmentMeta>
                  <InlineDue>
                    <FiCalendar size={14} />
                    Due {assignment.due_at ? formatDate(assignment.due_at) : 'No due date'}
                  </InlineDue>
                  <InlineSeparator>·</InlineSeparator>
                  <InlineDescription>{plainDescription}</InlineDescription>
                </InlineAssignmentMeta>

                <AssignmentContent>
                  {assignment.quiz_description && (
                    <AssignmentDescription
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtmlForViewer(assignment.quiz_description || 'No description provided')
                      }}
                    />
                  )}

                  <AssignmentDetails>
                    <DetailItem>
                      <FiCalendar size={14} />
                      Due: {assignment.due_at ? formatDate(assignment.due_at) : 'No due date'}
                    </DetailItem>
                    <DetailItem>
                      <FiClock size={14} />
                      Time limit: {assignment.time_limit_minutes ? `${assignment.time_limit_minutes} minutes` : 'No time limit'}
                    </DetailItem>
                    <DetailItem>
                      <FiUsers size={14} />
                      Attempts allowed: {assignment.attempts_allowed || 1}
                    </DetailItem>
                    <DetailItem>
                      <FiFileText size={14} />
                      {completedAttempts}/{totalAttempts} completed
                    </DetailItem>
                  </AssignmentDetails>

                  <AssignmentDivider />

                  <AssignmentActions>
                    <ActionButton
                      className="primary"
                      onClick={() => navigate(`/instructor/quizzes/${assignment.quiz_id}/attempts`)}
                    >
                      <FiUsers size={16} />
                      View submissions
                    </ActionButton>
                    <ActionButton
                      className="secondary"
                      onClick={() => handleEditAssignment(assignment)}
                    >
                      <FiEdit3 size={16} />
                      Edit
                    </ActionButton>
                    <ActionButton
                      className="secondary"
                      onClick={() => navigate(`/instructor/quizzes/${assignment.quiz_id}`)}
                    >
                      <FiEye size={16} />
                      Preview
                    </ActionButton>
                    <ActionButton
                      className="secondary"
                      onClick={() => handleResetStudentQuiz(assignment)}
                    >
                      <FiRefreshCw size={16} />
                      Reset attempts
                    </ActionButton>
                    <ActionSpacer />
                    <ActionButton
                      className="danger"
                      onClick={() => handleUnassignQuiz(assignment)}
                      aria-label="Unassign exam"
                    >
                      <FiTrash2 size={16} />
                      Unassign
                    </ActionButton>
                  </AssignmentActions>
                </AssignmentContent>
              </AssignmentCard>
            );
          })}
        </AssignmentsList>
      ) : (
        <EmptyState>
          <FiUsers size={64} />
          <h3>No assigned quizzes found</h3>
          <p>
            {courseFilter === 'active'
              ? 'You haven\'t assigned any quizzes to students yet. Create and assign quizzes to see them here.'
              : courseFilter === 'inactive'
              ? 'No assigned quizzes found for inactive courses.'
              : 'No assigned quizzes found for the selected filter.'}
          </p>
        </EmptyState>
      )}
    </>
  );

  const renderHelpTab = () => (
    <HelpContainer>
      <HelpHeader>
        <HelpTitle>Quiz Management Guide</HelpTitle>
        <HelpSubtitle>Complete guide to creating, assigning, and managing quizzes</HelpSubtitle>
      </HelpHeader>

      <HelpSection>
        <HelpSectionTitle>📝 Creating a Quiz</HelpSectionTitle>
        <HelpStep>
          <StepNumber>1</StepNumber>
          <StepContent>
            <StepTitle>Click "Create Quiz"</StepTitle>
            <StepDescription>
              Click the "Create Quiz" button in the top-right corner of the page to start creating a new quiz.
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>2</StepNumber>
          <StepContent>
            <StepTitle>Fill in Quiz Details</StepTitle>
            <StepDescription>
              Enter the quiz title and description. The description will be visible to students when they take the quiz.
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>3</StepNumber>
          <StepContent>
            <StepTitle>Add Questions</StepTitle>
            <StepDescription>
              Use the "Add Multiple Choice", "Add True/False", "Add Fill in the Blank", or "Add Matching" buttons to add questions to your quiz. Matching questions allow you to create two lists where students pair items from one list with items in the other.
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>4</StepNumber>
          <StepContent>
            <StepTitle>Configure Question Settings</StepTitle>
            <StepDescription>
              For each question, set the points value, mark as extra credit if desired, and configure case sensitivity for fill-in-the-blank questions.
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>5</StepNumber>
          <StepContent>
            <StepTitle>Save Your Quiz</StepTitle>
            <StepDescription>
              Click "Save Quiz" to save your quiz. You can edit it later or assign it to students.
            </StepDescription>
          </StepContent>
        </HelpStep>
      </HelpSection>

      <HelpSection>
        <HelpSectionTitle>👥 Assigning Quizzes</HelpSectionTitle>
        <HelpStep>
          <StepNumber>1</StepNumber>
          <StepContent>
            <StepTitle>Click "Assign" Button</StepTitle>
            <StepDescription>
              In the "All Quizzes" tab, click the "Assign" button next to the quiz you want to assign.
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>2</StepNumber>
          <StepContent>
            <StepTitle>Select Students</StepTitle>
            <StepDescription>
              Choose which students should receive this quiz assignment. You can select individual students or entire courses.
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>3</StepNumber>
          <StepContent>
            <StepTitle>Set Assignment Parameters</StepTitle>
            <StepDescription>
              Configure the assignment settings:
              <ul>
                <li><strong>Time Limit:</strong> Set how long students have to complete the quiz</li>
                <li><strong>Due Date:</strong> Set when the assignment is due</li>
                <li><strong>Attempts Allowed:</strong> Set how many times students can attempt the quiz</li>
                <li><strong>Immediate Feedback:</strong> Choose whether students see results immediately</li>
              </ul>
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>4</StepNumber>
          <StepContent>
            <StepTitle>Confirm Assignment</StepTitle>
            <StepDescription>
              Review your assignment settings and click "Assign Quiz" to send the quiz to selected students.
            </StepDescription>
          </StepContent>
        </HelpStep>
      </HelpSection>

      <HelpSection>
        <HelpSectionTitle>📊 Viewing Submissions</HelpSectionTitle>
        <HelpStep>
          <StepNumber>1</StepNumber>
          <StepContent>
            <StepTitle>Access Quiz Submissions</StepTitle>
            <StepDescription>
              In the "All Quizzes" tab, click "View Submissions" next to any quiz to see all student submissions.
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>2</StepNumber>
          <StepContent>
            <StepTitle>Review Submission Details</StepTitle>
            <StepDescription>
              Click "View Details" next to any student submission to see their complete quiz attempt, including:
              <ul>
                <li>All answers provided</li>
                <li>Correct/incorrect responses</li>
                <li>Points awarded</li>
                <li>Time taken</li>
              </ul>
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>3</StepNumber>
          <StepContent>
            <StepTitle>Override Student Answers</StepTitle>
            <StepDescription>
              If needed, you can override student answers by clicking "Edit Answer" on any question. You must provide a reason for the override.
            </StepDescription>
          </StepContent>
        </HelpStep>
      </HelpSection>

      <HelpSection>
        <HelpSectionTitle>⚙️ Managing Quizzes</HelpSectionTitle>
        <HelpStep>
          <StepNumber>1</StepNumber>
          <StepContent>
            <StepTitle>Edit Quiz Content</StepTitle>
            <StepDescription>
              Click "Edit" next to any quiz to modify questions, points, or quiz settings. Changes will apply to future attempts.
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>2</StepNumber>
          <StepContent>
            <StepTitle>Preview Quiz</StepTitle>
            <StepDescription>
              Click "Preview" to see how the quiz will appear to students before assigning it.
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>3</StepNumber>
          <StepContent>
            <StepTitle>Delete Quiz</StepTitle>
            <StepDescription>
              Click "Delete" to remove a quiz permanently. This will also delete all associated submissions and cannot be undone.
            </StepDescription>
          </StepContent>
        </HelpStep>
      </HelpSection>

      <HelpSection>
        <HelpSectionTitle>📋 Assigned Quizzes Tab</HelpSectionTitle>
        <HelpStep>
          <StepNumber>1</StepNumber>
          <StepContent>
            <StepTitle>View All Assignments</StepTitle>
            <StepDescription>
              The "Assigned Quizzes" tab shows all quizzes that have been assigned to students, organized by course.
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>2</StepNumber>
          <StepContent>
            <StepTitle>Edit Assignment Settings</StepTitle>
            <StepDescription>
              Click "Edit Assignment" to modify time limits, due dates, or other assignment parameters.
            </StepDescription>
          </StepContent>
        </HelpStep>
        
        <HelpStep>
          <StepNumber>3</StepNumber>
          <StepContent>
            <StepTitle>Unassign Quiz</StepTitle>
            <StepDescription>
              Click "Unassign" to remove a quiz assignment from students. This will delete all existing attempts.
            </StepDescription>
          </StepContent>
        </HelpStep>
      </HelpSection>


      <HelpSection>
        <HelpSectionTitle>❓ Troubleshooting</HelpSectionTitle>
        <HelpTroubleshooting>
          <TroubleshootingItem>
            <TroubleshootingTitle>Student can't see assigned quiz</TroubleshootingTitle>
            <TroubleshootingSolution>
              Check that the quiz is published and the assignment due date hasn't passed. Verify the student is in the correct course.
            </TroubleshootingSolution>
          </TroubleshootingItem>
          
          <TroubleshootingItem>
            <TroubleshootingTitle>Quiz timer not working</TroubleshootingTitle>
            <TroubleshootingSolution>
              Ensure a time limit was set when assigning the quiz. Check that the student's browser allows JavaScript and isn't blocking timers.
            </TroubleshootingSolution>
          </TroubleshootingItem>
          
          <TroubleshootingItem>
            <TroubleshootingTitle>Submissions not showing up</TroubleshootingTitle>
            <TroubleshootingSolution>
              Refresh the page and check that the quiz was properly assigned. Look in the "Assigned Quizzes" tab to verify assignment status.
            </TroubleshootingSolution>
          </TroubleshootingItem>
          
          <TroubleshootingItem>
            <TroubleshootingTitle>Can't edit quiz after assignment</TroubleshootingTitle>
            <TroubleshootingSolution>
              You can edit quiz content at any time, but changes only affect new attempts. Existing submissions will retain their original scoring.
            </TroubleshootingSolution>
          </TroubleshootingItem>
        </HelpTroubleshooting>
      </HelpSection>

    </HelpContainer>
  );

  return (
    <AssignmentsContainer>
      <MobileTopPanel>
        <Header>
          <HeaderText>
            <Title>Quizzes</Title>
            <HeaderSubtitle>{quizCountByCourseFilter} total · {assignedQuizCountByCourseFilter} assigned</HeaderSubtitle>
          </HeaderText>
          <ActionButton
            className="primary"
            onClick={() => navigate('/instructor/quizzes/new')}
          >
            <FiPlus size={16} />
            New quiz
          </ActionButton>
        </Header>

        <TabList>
          <Tab 
            $active={activeTab === 'quizzes'} 
            data-active={activeTab === 'quizzes'}
            onClick={() => setActiveTab('quizzes')}
          >
            <DesktopTabText>All Quizzes</DesktopTabText>
            <TabCount>{quizCountByCourseFilter}</TabCount>
          </Tab>
          <Tab 
            $active={activeTab === 'assigned'} 
            data-active={activeTab === 'assigned'}
            onClick={() => setActiveTab('assigned')}
          >
            <DesktopTabText>Assigned Quizzes</DesktopTabText>
            <TabCount>{assignedQuizCountByCourseFilter}</TabCount>
          </Tab>
          <MobileHelpTab
            $active={activeTab === 'help'} 
            onClick={() => setActiveTab('help')}
          >
            <FiHelpCircle size={14} />
            Help
          </MobileHelpTab>
        </TabList>

        {activeTab === 'quizzes' && (
          <QuizFiltersBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            courseFilter={courseFilter}
            onCourseFilterChange={setCourseFilter}
            courses={courses}
            sortBy={sortBy}
            onSortChange={setSortBy}
          />
        )}

        {activeTab === 'assigned' && (
          <QuizFiltersBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            courseFilter={courseFilter}
            onCourseFilterChange={setCourseFilter}
            courses={courses}
            sortBy={sortBy}
            onSortChange={setSortBy}
          />
        )}
      </MobileTopPanel>

      <TabContainer>
        <TabPanel $active={activeTab === 'quizzes'}>
          {renderQuizzesTab()}
        </TabPanel>

        <TabPanel $active={activeTab === 'assigned'}>
          {renderAssignedQuizzesTab()}
        </TabPanel>

        <TabPanel $active={activeTab === 'help'}>
          {renderHelpTab()}
        </TabPanel>
      </TabContainer>

      <EditAssignmentModal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        assignment={selectedAssignment}
        onSave={handleSaveAssignment}
      />

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={cancelDeleteQuiz}
        onConfirm={confirmDeleteQuiz}
        title="Delete Quiz"
        message={`Are you sure you want to delete "${quizToDelete?.title}"?`}
        details="This action cannot be undone and will also delete all quiz attempts and submissions."
        confirmText="Delete Quiz"
        cancelText="Cancel"
        isLoading={deleteLoading}
        type="danger"
      />

      <UnassignQuizModal
        isOpen={unassignModalOpen}
        onClose={cancelUnassignQuiz}
        onConfirm={handleConfirmUnassign}
        quizId={assignmentToUnassign?.quiz_id}
        quizTitle={assignmentToUnassign?.quiz_title}
      />

      <ResetStudentQuizModal
        isOpen={resetModalOpen}
        onClose={cancelResetQuiz}
        onConfirm={handleConfirmReset}
        quizId={assignmentToReset?.quiz_id}
        quizTitle={assignmentToReset?.quiz_title}
      />

      <SuccessModal
        isOpen={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        title="Success"
        message={successMessage}
        details={successDetails}
      />

      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onConfirm={() => setShowErrorModal(false)}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText={null}
        type="danger"
      />
    </AssignmentsContainer>
  );
}

export default InstructorQuizList;
